import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useCreateInventoryItem, useUpdateInventoryItem } from '@/features/doer/use-inventory'
import type { ApiError, InventoryItem } from '@/api/types'

interface InventoryEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** When provided, editing existing item; otherwise creating new. */
  item?: InventoryItem | null
}

const apiErrorMessage = (err: unknown, fallback: string): string =>
  (err as ApiError | undefined)?.detail ?? fallback

export const InventoryEditDialog = ({ open, onOpenChange, item }: InventoryEditDialogProps) => {
  const create = useCreateInventoryItem()
  const update = useUpdateInventoryItem()
  const [name, setName] = useState('')
  const [unit, setUnit] = useState('')
  const [quantity, setQuantity] = useState('')
  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (open) {
      setName(item?.name ?? '')
      setUnit(item?.unit ?? '')
      setQuantity(item?.quantity != null ? String(item.quantity) : '')
      setNotes(item?.notes ?? '')
    }
  }, [open, item])

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    const trimmedName = name.trim()
    const trimmedUnit = unit.trim()
    const qty = Number(quantity)
    if (!trimmedName || !trimmedUnit || !Number.isFinite(qty) || qty <= 0) {
      toast.error('Name, unit, and a positive quantity are required')
      return
    }

    const payload = { name: trimmedName, unit: trimmedUnit, quantity: qty, notes: notes || null }
    const onSuccess = () => {
      toast.success(item ? 'Inventory updated' : 'Added to inventory')
      onOpenChange(false)
    }
    const onError = (err: unknown) => {
      toast.error(apiErrorMessage(err, item ? 'Update failed' : 'Could not add'))
    }

    if (item) {
      update.mutate({ id: item.id, data: payload }, { onSuccess, onError })
    } else {
      create.mutate(payload, { onSuccess, onError })
    }
  }

  const isPending = create.isPending || update.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{item ? 'Edit inventory item' : 'Add inventory item'}</DialogTitle>
          <DialogDescription>
            Used to subtract owned quantities from your personalized BOM. Match is by name + unit
            (case-insensitive).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="inv-name">Name</Label>
            <Input
              id="inv-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. 4x4 cedar"
              required
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="inv-unit">Unit</Label>
              <Input
                id="inv-unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="ft, ea, gal"
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="inv-qty">Quantity</Label>
              <Input
                id="inv-qty"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                inputMode="decimal"
                placeholder="0"
                required
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="inv-notes">Notes (optional)</Label>
            <Textarea
              id="inv-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving…' : item ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
